import { Injectable } from "@angular/core";
import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpEventType,
  HttpHeaders,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from "@angular/common/http";
import {
  forkJoin,
  from,
  lastValueFrom,
  Observable,
  of,
  throwError,
} from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";
import { Endpoint } from "../models";
import { environment } from "../../environments/environment";
import type { IApiPermissionParams, IApiResponse } from "../models/http";
import { HTTP_METHOD } from "./api";
import { UserService } from "./user.service";
import { AlertService } from "./alert.service";

@Injectable({
  providedIn: "root",
})
export class HttpService {
  #GET_USER_PERMISSION = "GetUserPermissions";

  constructor(
    private readonly client: HttpClient,
    private readonly us: UserService,
    private readonly alert: AlertService
  ) {}

  /**
   * General request point
   * @param method
   * @param endpoint
   * @param param2
   * @returns
   */
  request$<T>(
    method: HTTP_METHOD,
    endpoint: Endpoint,
    { body, params, skipCheck = true }: _HttpRequestProps
  ): Observable<IApiResponse<T>> {
    return this._doPermissionCheck(skipCheck, endpoint, params).pipe(
      switchMap(({ token }) => {
        const url = environment.apiBaseUrl + endpoint;
        const requestHeaders = this._getHeaders(token);
        const requestParams = this._getHttpParams(params);
        const requestBody =
          body && method !== HTTP_METHOD.GET ? body : undefined;

        return this.client
          .request<IApiResponse<T>>(method, url, {
            body: requestBody,
            headers: requestHeaders,
            params: requestParams,
            observe: "response",
            responseType: "json",
          })
          .pipe(
            map((res) => {
              const body = res.body as IApiResponse<T>;

              if (body.success) {
                return body as IApiResponse<T>;
              }

              throw new Error(this._handleSuccessFalseMessage(res));
            }),
            catchError((err: HttpErrorResponse | any) => this._handleError(err))
          );
      })
    );
  }

  /**
   * POST to the report server
   *
   * @param endpoint
   * @param body
   * @returns
   */
  report$(endpoint: Endpoint, body: any): Observable<IApiResponse> {
    return from(this.us.getUserToken()).pipe(
      switchMap((token) => {
        const url = environment.reportServerUrl + endpoint;
        const requestHeaders = this._getReportHeaders(token);

        return this.client
          .request<IApiResponse>(HTTP_METHOD.POST, url, {
            body,
            headers: requestHeaders,
            observe: "response",
            responseType: "json",
          })
          .pipe(
            map((res) => {
              const body = res.body as IApiResponse;

              if (body.success) {
                return body as IApiResponse;
              }

              throw new Error(this._handleSuccessFalseMessage(res));
            }),
            catchError((err: HttpErrorResponse | any) => this._handleError(err))
          );
      })
    );
  }

  /**
   * Checks user permissions for a given endpoint.
   *
   * @param {boolean} skipCheck - Flag to determine if the permission check should be skipped.
   * @param {Endpoint} endpoint - The API endpoint to check permissions for.
   * @param {Record<string, any>} [params] - Optional query parameters that may include projectId or projectGuid.
   *
   * @returns {Observable<{ token: string | null; permissionGranted: boolean }>}
   * An observable that emits an object containing the user's token and a boolean indicating if permission is granted.
   */
  private _doPermissionCheck(
    skipCheck: boolean,
    endpoint: Endpoint,
    params?: Record<string, any>
  ): Observable<{ token: string | null }> {
    return forkJoin([
      this.us.checkAndGetUserIds({ returnNullOnError: true }),
      from(this.us.getUserToken()),
    ]).pipe(
      switchMap(([ids, token]) => {
        if (skipCheck) {
          return of({ token, permissionGranted: true });
        }

        if (token === null || ids.tenantId === null || ids.userId === null) {
          return of({ token, permissionGranted: false });
        }

        const query: IApiPermissionParams = {
          endpoint,
          userId: `${ids.userId ?? -1}`,
          tenantId: `${ids.tenantId ?? -1}`,
        };

        if (params && params["projectId"]) {
          query.projectId = params["projectId"];
        }

        if (params && params["projectId"]) {
          query.projectGuid = params["projectGuid"];
        }

        return this._getUserPermission(query, token).pipe(
          map((permissionGranted) => {
            return { token, permissionGranted };
          })
        );
      }),
      switchMap(({ token, permissionGranted }) => {
        if (!permissionGranted) {
          this.alert.showConfirm(
            "Access Denied",
            "",
            "You do not have permission to access this resource.",
            "OK",
            async () => this.us.logout()
          );
          return throwError(() => new Error("Access Denied"));
        }
        return of({ token });
      }),
      catchError((err: HttpErrorResponse | any) => {
        const message = err?.error?.message ?? err?.message ?? "Unknown error";
        return throwError(() => message);
      })
    );
  }

  /**
   * Checks user permission by sending a GET request to the permissions endpoint.
   *
   * @param query - The parameters for the permissions check including endpoint, userId, and tenantId.
   * @param token - The authentication token for the request.
   * @returns An Observable that emits a boolean indicating whether permission is granted.
   */
  private _getUserPermission(
    query: IApiPermissionParams,
    token: string
  ): Observable<boolean> {
    return this.client
      .get<IApiResponse>(environment.apiBaseUrl + this.#GET_USER_PERMISSION, {
        params: query as any,
        headers: {
          "Ocp-Apim-Subscription-Key": environment.apiSubscriptionKey,
          token: token || "",
        },
      })
      .pipe(map((res) => res.success ?? false));
  }

  /**
   * Uploads files to the specified endpoint.
   *
   * @param {Endpoint} endpoint - The endpoint to upload the files to.
   * @param {Object | boolean | null} fields - The fields to include in the upload request.
   * @param {File[]} files - The files to upload.
   * @return {Promise<IApiResponse<T>>} A promise that resolves to the API response.
   */
  async uploadFiles<T>(
    endpoint: Endpoint,
    fields: { [key: string]: any } | boolean | null,
    files: File[]
  ): Promise<IApiResponse<T>> {
    if (!environment.production) {
      console.log("HTTP REQUEST /", endpoint);
    }
    try {
      const url = environment.apiBaseUrl + endpoint;
      const token = await this.us.getUserToken();
      const httpHeaders = this._getUploadHeaders(token);
      const formData = new FormData();

      files.forEach((file: File) => {
        formData.append("file", file);
      });

      if (fields && typeof fields === "object") {
        Object.keys(fields).forEach((key) => formData.append(key, fields[key]));
      }
      // Get and Post/Put/Delete options are diff, see types below
      const options: _AngularGetRequestOptions | _AngularRequestOptions | any =
        {
          reportProgress: false,
          headers: httpHeaders,
          responseType: "json",
          observe: "response",
          body: fields,
        };

      const req = new HttpRequest(HTTP_METHOD.POST, url, formData, options);

      // Make the request
      //
      const ret = (await lastValueFrom(
        this.client.request(req).pipe(
          // Filter for the final response
          filter((event) => event.type === HttpEventType.Response),
          // Cast to HttpResponse
          map((event) => event as HttpResponse<{ data: IApiResponse<T> }>)
        )
      )) as HttpResponse<any>;

      // Process HTTP Result
      //
      if (ret.status === 200) {
        const bodyData: IApiResponse<T> = ret.body.data;
        if (bodyData?.success === true) {
          return Promise.resolve(bodyData);
        } else {
          const errMsg = this._handleSuccessFalseMessage(ret);
          return Promise.reject(errMsg);
        }
      }

      return Promise.reject(
        "Network request failed with status #" +
          `${ret ? (ret as any).status : "ERR"}`
      );
    } catch (err) {
      if (err === "network-request-failed") {
        const errResponse: IApiResponse = { success: false, message: err };
        return Promise.resolve(errResponse as IApiResponse<T>);
      }
      return Promise.reject(err);
    }
  }

  /**
   * Handle the available message options for success = false
   * @param ret http server response
   * @returns string message for error rejection
   */
  private _handleSuccessFalseMessage(ret: HttpResponse<any>): string {
    let retBody: IApiResponse | undefined = ret.body ?? ret.body?.data ?? ret;
    if (retBody?.message) {
      return `${retBody?.message} (Status #${ret.status})`;
    } else if (ret && (ret as any).error?.message) {
      return `${(ret as any).error.message} (Status #${ret.status})`;
    }
    return `Unknown Error (Status #${ret.status})`;
  }

  /**
   * Cleans up params object and returns HttpParams
   * @param params
   * @returns
   */
  private _getHttpParams(params?: Record<string, any>): HttpParams {
    const result = new HttpParams({
      fromObject: Object.entries(params || {}).reduce((acc, [key, val]) => {
        if (val !== null && val !== undefined) acc[key] = `${val}`;
        return acc;
      }, {} as Record<string, string>),
    });
    return result;
  }

  /**
   * Generate the HttpHeaders for a standard api request
   * @param token
   * @returns
   */
  private _getHeaders(token: string | null): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.append("Content-Type", "application/json");
    httpHeaders = httpHeaders.append(
      "Ocp-Apim-Subscription-Key",
      environment.apiSubscriptionKey
    );
    httpHeaders = httpHeaders.append("Ocp-Apim-Trace", "false");
    if (token) {
      httpHeaders = httpHeaders.append("token", token);
    }
    return httpHeaders;
  }

  /**
   * Generate the HttpHeaders for a standard api request
   * @param token
   * @returns
   */
  private _getReportHeaders(token: string | null): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.append("Content-Type", "application/json");
    httpHeaders = httpHeaders.append(
      "Ocp-Apim-Subscription-Key",
      environment.reportSubscriptionKey
    );
    httpHeaders = httpHeaders.append("Ocp-Apim-Trace", "false");
    if (token) {
      httpHeaders = httpHeaders.append("token", token);
    }
    return httpHeaders;
  }

  /**
   * Generate the HttpHeaders for multipart-file uploads
   * @param token
   * @returns
   */
  private _getUploadHeaders(token: string | null): HttpHeaders {
    let httpHeaders = new HttpHeaders();
    httpHeaders = httpHeaders.append(
      "Ocp-Apim-Subscription-Key",
      environment.apiSubscriptionKey
    );
    httpHeaders = httpHeaders.append("Ocp-Apim-Trace", "false");
    if (token) {
      httpHeaders = httpHeaders.append("token", token);
    }
    return httpHeaders;
  }

  private _handleError(err: HttpErrorResponse | any): Observable<any> {
    let message = "Unknown error";

    if (err.statusText === "network-request-failed") {
      return of({
        success: false,
        message: err.error.message,
      } as IApiResponse);
    }

    if (err instanceof HttpErrorResponse) {
      if (err.status === 0) {
        message = "Network error or server unreachable.";
      } else if (typeof err.error === "string") {
        message = err.error;
      } else if (err.error?.message) {
        message = err.error.message;
      } else {
        message = `HTTP ${err.status}: ${err.statusText}`;
      }
    } else if (err instanceof Error) {
      message = err.message;
    } else if (typeof err === "string") {
      message = err;
    }

    return throwError(() => message);
  }
}

type _HttpRequestProps = {
  params?: Record<string, any>;
  body?: Record<string, any>;
  skipCheck?: boolean;
};

type _AngularRequestOptions = {
  body?: any;
  headers?: HttpHeaders;
  context?: HttpContext;
  observe?: "response" | "body" | "events" | undefined;
  responseType: "json" | "arraybuffer" | "blob" | "text" | undefined;
  params?: HttpParams;
  reportProgress?: boolean;
  withCredentials?: boolean;
};

type _AngularGetRequestOptions = {
  headers?: HttpHeaders;
  context?: HttpContext;
  observe?: "body" | undefined;
  responseType: "json" | undefined;
  params?: HttpParams;
  reportProgress?: boolean;
  withCredentials?: boolean;
};
